import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, MapPin } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Textarea } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { MedicalLookup, EMPTY_MEDICAL, medicalToPayload, type MedicalValues } from "./MedicalLookup";

// =============================================================================
// NewSiteScreen — /sites/new
// Full-page site creation with all schema fields grouped into sections. Only
// site_name is required; everything else is optional and editable later from
// the detail page. Manager+ only (RLS also enforces). T-037.
// =============================================================================

const SITE_TYPES = [
  "Lattice Tower",
  "Monopole",
  "Guyed Mast",
  "Rooftop",
  "Watertank",
  "Other",
] as const;

const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"] as const;

const MOBILE_COVERAGE = ["good", "marginal", "none"] as const;

export default function NewSiteScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canCreate = user?.role === "director" || user?.role === "manager";

  // Identity
  const [site_name, setSiteName] = useState("");
  const [site_code, setSiteCode] = useState("");
  const [rfnsa_id, setRfnsaId] = useState("");
  const [carrier_site_id, setCarrierSiteId] = useState("");
  const [site_type, setSiteType] = useState("");
  const [structure_height_m, setStructureHeight] = useState("");
  const [asset_owner_id, setAssetOwnerId] = useState("");

  // Auto-suggest a clean site code from the site name
  const suggestedCode = site_name
    .toUpperCase()
    .replace(/[^A-Z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40);

  const { data: assetOwners = [] } = useQuery({
    queryKey: ["asset_owners_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_owners")
        .select("asset_owner_id, owner_code, display_name")
        .eq("is_active", true)
        .order("display_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Location
  const [address_line1, setAddr1] = useState("");
  const [address_line2, setAddr2] = useState("");
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [what3words, setW3W] = useState("");
  const [land_owner_name, setLandOwner] = useState("");

  // Operational
  const [access_details, setAccess] = useState("");
  const [access_restrictions, setRestrictions] = useState("");
  const [parking_notes, setParking] = useState("");
  const [key_location, setKeys] = useState("");

  // Emergency + safety
  const [hazards_summary, setHazards] = useState("");
  // hospital + medical centre — the shared lookup block (mig 395, 27 Sep 2026)
  const [med, setMed] = useState<MedicalValues>(EMPTY_MEDICAL);
  const [mobile_coverage, setCoverage] = useState("");

  // Notes
  const [notes, setNotes] = useState("");

  const [errMsg, setErrMsg] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!site_name.trim()) throw new Error("Site name is required.");
      const payload = {
        site_name: site_name.trim(),
        rfnsa_id: rfnsa_id.trim() || null,
        carrier_site_id: carrier_site_id.trim() || null,
        site_type: site_type || null,
        structure_height_m: structure_height_m.trim() ? parseFloat(structure_height_m) : null,
        asset_owner_id: asset_owner_id || null,
        address_line1: address_line1.trim() || null,
        address_line2: address_line2.trim() || null,
        suburb: suburb.trim() || null,
        state: state.trim() || null,
        postcode: postcode.trim() || null,
        latitude:  latitude.trim()  ? parseFloat(latitude)  : null,
        longitude: longitude.trim() ? parseFloat(longitude) : null,
        what3words: what3words.trim() || null,
        land_owner_name: land_owner_name.trim() || null,
        access_details: access_details.trim() || null,
        access_restrictions: access_restrictions.trim() || null,
        parking_notes: parking_notes.trim() || null,
        key_location: key_location.trim() || null,
        hazards_summary: hazards_summary.trim() || null,
        ...medicalToPayload(med),
        mobile_coverage: mobile_coverage || null,
        notes: notes.trim() || null,
        is_active: true,
        site_code: (site_code.trim() || suggestedCode || "").trim(),
      };
      // (supabase as any) — nearest_medical_centre_* (mig 395) not in database.types.ts yet
      const { data, error } = await (supabase as any)
        .from("sites")
        .insert(payload)
        .select("site_id")
        .single();
      if (error) throw error;
      return data.site_id as string;
    },
    onSuccess: (siteId) => {
      qc.invalidateQueries({ queryKey: ["sites_list"] });
      qc.invalidateQueries({ queryKey: ["sites_picker"] });
      navigate(`/sites/${siteId}`);
    },
    onError: (e: unknown) => setErrMsg(e instanceof Error ? e.message : "Failed to create site"),
  });

  if (!canCreate) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-text-muted">
            Only managers and directors can create sites. Ask one of them to add this site.
          </div>
        </Card>
      </div>
    );
  }

  const canSubmit = !!site_name.trim() && !createMut.isPending;

  return (
    <div className="p-6 max-w-3xl mx-auto pb-24">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mb-5">
        <MapPin size={20} className="text-accent" />
        New site
      </h1>

      <Card>
        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Identity</div>
          <div className="space-y-3">
            <Field label="Site name *" hint="Free-form, e.g. 'Toowoomba CBD rooftop' or 'Toowong exchange'.">
              <Input value={site_name} onChange={e => setSiteName(e.target.value)} autoFocus placeholder="Site name" />
            </Field>
            <Field label="Site code" hint={`Short uppercase identifier used everywhere (BEECHMONT, ANDERGROVE-NORTH, BADGERYS-CREEK-VF). Leave blank to auto-generate. Suggested: ${suggestedCode || "(type a name first)"}`}>
              <Input
                mono
                value={site_code}
                onChange={e => setSiteCode(e.target.value.toUpperCase())}
                placeholder={suggestedCode || "BADGERYS-CREEK-VF"}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RFNSA ID" hint="National RF Site Archive reference.">
                <Input type="number" mono value={rfnsa_id} onChange={e => setRfnsaId(e.target.value)} placeholder="e.g. 4350003" />
              </Field>
              <Field label="Carrier site ID" hint="Carrier's internal code if known.">
                <Input mono value={carrier_site_id} onChange={e => setCarrierSiteId(e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Site type">
                <Select value={site_type} onChange={e => setSiteType(e.target.value)}>
                  <option value="">— none —</option>
                  {SITE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Structure height (m)" hint="Top of tower / monopole / building.">
                <Input
                  type="number"
                  step="0.01"
                  mono
                  value={structure_height_m}
                  onChange={e => setStructureHeight(e.target.value)}
                  placeholder="e.g. 30.00"
                />
              </Field>
              <Field label="Asset owner" hint="Who owns the tower/pit/exchange.">
                <Select value={asset_owner_id} onChange={e => setAssetOwnerId(e.target.value)}>
                  <option value="">— none —</option>
                  {assetOwners.map(ao => (
                    <option key={ao.asset_owner_id} value={ao.asset_owner_id}>
                      {ao.owner_code} · {ao.display_name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Location</div>
          <div className="space-y-3">
            <Field label="Address line 1">
              <Input value={address_line1} onChange={e => setAddr1(e.target.value)} placeholder="Street address" />
            </Field>
            <Field label="Address line 2">
              <Input value={address_line2} onChange={e => setAddr2(e.target.value)} placeholder="Unit, building, etc." />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Suburb">
                <Input value={suburb} onChange={e => setSuburb(e.target.value)} />
              </Field>
              <Field label="State">
                <Select value={state} onChange={e => setState(e.target.value)}>
                  <option value="">—</option>
                  {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Postcode">
                <Input mono value={postcode} onChange={e => setPostcode(e.target.value)} maxLength={4} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude *" hint="Critical for remote sites where addresses are unreliable. Used to plot the dot on the Australia map. Decimal degrees, e.g. -33.8688">
                <Input mono value={latitude} onChange={e => setLatitude(e.target.value)} placeholder="-33.8688" />
              </Field>
              <Field label="Longitude *" hint="Decimal degrees, e.g. 151.2093">
                <Input mono value={longitude} onChange={e => setLongitude(e.target.value)} placeholder="151.2093" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="what3words" hint="Emergency locating aid.">
                <Input mono value={what3words} onChange={e => setW3W(e.target.value)} placeholder="///word.word.word" />
              </Field>
              <Field label="Land owner name">
                <Input value={land_owner_name} onChange={e => setLandOwner(e.target.value)} placeholder="Private / Council / NBN Co" />
              </Field>
            </div>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Operational</div>
          <div className="space-y-3">
            <Field label="Access details">
              <Textarea rows={2} value={access_details} onChange={e => setAccess(e.target.value)} placeholder="Gate codes, contact person, site hours…" />
            </Field>
            <Field label="Access restrictions">
              <Textarea rows={2} value={access_restrictions} onChange={e => setRestrictions(e.target.value)} placeholder="Permit to work, induction required, escorted only…" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Parking notes">
                <Input value={parking_notes} onChange={e => setParking(e.target.value)} />
              </Field>
              <Field label="Key location">
                <Input value={key_location} onChange={e => setKeys(e.target.value)} />
              </Field>
            </div>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Emergency &amp; safety</div>
          <div className="space-y-3">
            <Field label="Hazards summary" hint="Full risk assessment happens in SWMS per project. This is the short summary.">
              <Textarea rows={2} value={hazards_summary} onChange={e => setHazards(e.target.value)} />
            </Field>
            <MedicalLookup lat={latitude} lng={longitude} address={[address_line1, suburb, state, postcode].filter(Boolean).join(", ")} value={med} onChange={setMed}/>
            <Field label="Mobile coverage">
              <Select value={mobile_coverage} onChange={e => setCoverage(e.target.value)}>
                <option value="">— unknown —</option>
                {MOBILE_COVERAGE.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
        </div>

        <div className="p-5">
          <div className="text-[13px] font-medium text-text mb-3">Notes</div>
          <Field label="Internal notes">
            <Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the crew should know." />
          </Field>
        </div>
      </Card>

      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center">
            {errMsg}
          </div>
        )}
        <div className="p-3 flex justify-center">
          <div className="flex gap-2 max-w-2xl w-full">
            <Button variant="secondary" size="lg" onClick={() => navigate(-1)} type="button">Cancel</Button>
            <Button
              size="lg"
              icon={CheckCircle2}
              onClick={() => createMut.mutate()}
              disabled={!canSubmit}
              className="flex-1 justify-center"
              type="button"
            >
              {createMut.isPending ? "Creating…" : "Create site"}
            </Button>
          </div>
        </div>
      </StickyBottomBar>
    </div>
  );
}
